import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { Resource } from '../../types'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import { useVersionControl } from '../../hooks/useVersionControl'
import { ConflictModal } from '../ui/ConflictModal'
import { BulkUploadModal } from '../shared/BulkUploadModal'
import s from './shared.module.css'

export default function Resources() {
  const { organization, user, orgMember } = useAuth()
  const { resources, resourceRoleTypes, orgManagers, refreshData } = useAppData()
  const orgId = organization!.id

  const canBulkUpload = orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin' || orgMember?.user_type === 'manager'
  const canManage = orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin' || orgMember?.user_type === 'manager'

  const [showModal, setShowModal] = useState(false)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [editing, setEditing] = useState<Resource | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [form, setForm] = useState({ title: '', email: '', skills: '', primary_role: '', availability: 8, active: true, manager_id: '' })

  const {
    saveWithVersionCheck,
    activeEditors,
    conflict,
    clearConflict
  } = useVersionControl<Resource & { version: number }>({
    tableName: 'resources',
    recordId: editing?.id || null
  })

  const openAdd = () => {
    setEditing(null)
    setForm({ title: '', email: '', skills: '', primary_role: '', availability: 8, active: true, manager_id: '' })
    setSaveError('')
    setShowModal(true)
  }

  const openEdit = (r: Resource) => {
    setEditing(r)
    setForm({ title: r.title, email: r.email, skills: r.skills, primary_role: r.primary_role, availability: r.availability, active: r.active, manager_id: r.manager_id || '' })
    setSaveError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setSaveError('')
    try {
      const payload = { ...form, manager_id: form.manager_id || null }
      if (editing) {
        const result = await saveWithVersionCheck(
          editing as Resource & { version: number },
          payload,
          `Updated resource: ${form.title}`
        )
        if (result.conflict) { setSaveError('Version conflict detected. Please review and resolve.'); return }
        if (!result.success) { setSaveError('Failed to save. Please try again.'); return }
      } else {
        await dataService.createResource(orgId, payload as any)
      }
      setShowModal(false)
      await refreshData()
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this resource?')) return
    await dataService.deleteResource(id)
    await refreshData()
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Resources</h2>
          <p className={s.pageSubtitle}>{resources.filter(r => r.active).length} active of {resources.length}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canBulkUpload && <button className={s.secondaryBtn} onClick={() => setShowBulkUpload(true)}>Bulk Upload</button>}
          {canManage && <button className={s.primaryBtn} onClick={openAdd}><Plus size={14} /> Add Resource</button>}
        </div>
      </div>

      <div className={s.card}>
       <div className={s.tableScroll}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Primary Role</th>
              <th>Manager</th>
              <th>Availability (hrs/day)</th>
              <th>Skills</th>
              <th>Status</th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {resources.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className={s.emptyState}>No resources found</td></tr>}
            {resources.map(r => {
              const manager = orgManagers.find(m => m.user_id === r.manager_id)
              return (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>{r.title}</td>
                  <td>{r.email || '—'}</td>
                  <td>{r.primary_role || '—'}</td>
                  <td style={{ color: manager ? '#323130' : '#a19f9d' }}>{manager ? `${manager.display_name}${manager.title ? ` — ${manager.title}` : ''}` : '—'}</td>
                  <td>{r.availability}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.skills || '—'}</td>
                  <td><Badge variant={r.active ? 'success' : 'neutral'}>{r.active ? 'Active' : 'Inactive'}</Badge></td>
                  {canManage && (
                    <td>
                      <div className={s.actions}>
                        <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(r)}><EditIcon size={14} /></button>
                        <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(r.id)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
       </div>
      </div>

      {showBulkUpload && (
        <BulkUploadModal
          entityType="resources"
          orgId={orgId}
          userId={user!.id}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => { setShowBulkUpload(false); refreshData() }}
        />
      )}

      <Modal open={showModal} title={editing ? 'Edit Resource' : 'Add Resource'} onClose={() => setShowModal(false)}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button><button className={s.primaryBtn} form="resourceForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="resourceForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          {editing && activeEditors.length > 0 && (
            <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '14px' }}>
              <strong>Currently being edited by:</strong> {activeEditors.filter(e => e.user_id !== user?.id).map(e => e.display_name).join(', ') || 'another user'}
            </div>
          )}
          <div className={s.formGroup}><label>Full Name *</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required /></div>
          <div className={s.formGroup}><label>Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Primary Role</label>
              <select value={form.primary_role} onChange={e => setForm(f => ({ ...f, primary_role: e.target.value }))}>
                <option value="">Select role...</option>
                {resourceRoleTypes.filter(rt => rt.active).map(rt => <option key={rt.id} value={rt.title}>{rt.title}</option>)}
              </select>
            </div>
            <div className={s.formGroup}><label>Availability (hrs/day) *</label><input type="number" min="1" max="24" value={form.availability} onChange={e => setForm(f => ({ ...f, availability: parseFloat(e.target.value) || 8 }))} required /></div>
          </div>
          <div className={s.formGroup}>
            <label>Reporting Manager</label>
            <select value={form.manager_id} onChange={e => setForm(f => ({ ...f, manager_id: e.target.value }))}>
              <option value="">No manager assigned (any manager can approve PTO)</option>
              {orgManagers.map(m => (
                <option key=  {m.id} value={m.user_id}>
                  {m.display_name}{m.title ? ` — ${m.title}` : ''} ({m.user_type})
                </option>
              ))}
            </select>
          </div>
          <div className={s.formGroup}><label>Skills</label><textarea value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} placeholder="e.g. React, TypeScript, Azure" /></div>
          <div className={s.checkboxGroup}><input type="checkbox" id="rActive" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /><label htmlFor="rActive">Active</label></div>
        </form>
      </Modal>

      {conflict && (
        <ConflictModal
          conflict={conflict}
          onReload={async () => { clearConflict(); await refreshData(); setSaving(false) }}
          onOverride={async () => { clearConflict(); setSaving(false) }}
          onCancel={() => { clearConflict(); setSaving(false) }}
        />
      )}
    </div>
  )
}
